import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { V3_SET_CART_UUID } from './v3Order';

// Action types
export const FETCH_CART_VOUCHER_ITEMS = 'FETCH_CART_VOUCHER_ITEMS';
export const ADD_VOUCHER_TO_CART = 'ADD_VOUCHER_TO_CART';
export const UPDATE_CART_VOUCHER_ITEM = 'UPDATE_CART_VOUCHER_ITEM';
export const REMOVE_CART_VOUCHER_ITEM = 'REMOVE_CART_VOUCHER_ITEM';
export const ACCEPT_VOUCHER_TERMS = 'ACCEPT_VOUCHER_TERMS';
export const CLEAR_CART_VOUCHER_ERRORS = 'CLEAR_CART_VOUCHER_ERRORS';
export const CLEAR_CART_VOUCHER_ITEMS = 'CLEAR_CART_VOUCHER_ITEMS';

// Fetch all voucher items in a cart
export function fetchCartVoucherItems(cartUuid) {
  return {
    type: FETCH_CART_VOUCHER_ITEMS,
    payload: axios.get(`/api/v3/carts/${cartUuid}/voucher_items`),
  };
}

// Resolve a single cart UUID shared with regular cart items.
// If v3Order.uuid is not yet set (voucher added before any regular product),
// generate one and persist it so subsequent voucher and product adds land in
// the same cart on the server.
function resolveCartUuid(dispatch, getState) {
  const existing = getState().v3Order?.uuid;
  if (existing) return existing;
  const fresh = uuidv4();
  dispatch({ type: V3_SET_CART_UUID, payload: fresh });
  return fresh;
}

// Add a voucher product to the cart
export function addVoucherToCart(
  voucherProductId,
  quantity = 1,
  termsAccepted = false,
  orderKey = null,
) {
  return async (dispatch, getState) => {
    const cartUuid = resolveCartUuid(dispatch, getState);
    try {
      const response = await axios.post(
        `/api/v3/carts/${cartUuid}/voucher_items`,
        {
          voucher_product_id: voucherProductId,
          quantity: quantity,
          order_key: orderKey,
        },
      );

      // If terms were accepted, also accept terms
      if (termsAccepted && response.data && response.data.id) {
        await axios.post(
          `/api/v3/carts/${cartUuid}/voucher_items/${response.data.id}/accept_terms`,
        );
      }

      dispatch({
        type: ADD_VOUCHER_TO_CART,
        payload: response,
      });

      return { success: true, data: response.data, cartUuid };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.errors?.join(', ') ||
        'Failed to add voucher to cart';

      dispatch({
        type: ADD_VOUCHER_TO_CART,
        error: true,
        payload: errorMessage,
      });

      return { error: errorMessage };
    }
  };
}

// Update quantity of a cart voucher item
export function updateCartVoucherItem(cartUuid, itemId, quantity) {
  return {
    type: UPDATE_CART_VOUCHER_ITEM,
    payload: axios.patch(`/api/v3/carts/${cartUuid}/voucher_items/${itemId}`, {
      quantity: quantity,
    }),
  };
}

// Remove a voucher item from the cart
export function removeCartVoucherItem(cartUuid, itemId) {
  return {
    type: REMOVE_CART_VOUCHER_ITEM,
    payload: axios.delete(`/api/v3/carts/${cartUuid}/voucher_items/${itemId}`),
    meta: { itemId },
  };
}

// Accept terms for a cart voucher item
export function acceptVoucherTerms(cartUuid, itemId) {
  return {
    type: ACCEPT_VOUCHER_TERMS,
    payload: axios.post(
      `/api/v3/carts/${cartUuid}/voucher_items/${itemId}/accept_terms`,
    ),
  };
}

// Clear errors
export function clearCartVoucherErrors() {
  return {
    type: CLEAR_CART_VOUCHER_ERRORS,
  };
}

// Clear all voucher items from cart (used when cart becomes empty)
export function clearCartVoucherItems(cartUuid) {
  // If we have a cart UUID, also clear on the backend
  if (cartUuid) {
    return {
      type: CLEAR_CART_VOUCHER_ITEMS,
      payload: axios.delete(
        `/api/v3/carts/${cartUuid}/voucher_items/clear_all`,
      ),
    };
  }
  // Otherwise just clear locally
  return {
    type: CLEAR_CART_VOUCHER_ITEMS,
  };
}
